import { ArrowRight, BookOpenCheck, FileText, Pencil } from 'lucide-react'
import type { MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import type { Document, Role } from '@agentwiki/shared'
import { SourceAuthorityReview } from '../authority/source-authority-review'
import { DocumentResultDetails, DocumentResultTerms } from '../shared/document-result-metadata'
import { buildDocumentResultPresentation } from '../../lib/document-result-presentation'
import { documentEditPath, documentReadPath } from '../../lib/document-route'
import type { KnowledgeLibraryReturnState } from '../../lib/knowledge-library-query-state'
import { captureDocumentReturn, returnLocationKey } from '../../lib/document-return-restoration'
import { cn } from '../../lib/utils'
import { getDocumentSourceAuthorityMetadata } from '../../lib/source-authority-metadata'

export function KnowledgeLibraryRow({
  doc,
  canEdit,
  returnState,
  role = null,
  originLocationKey,
}: {
  doc: Document
  canEdit: boolean
  returnState: KnowledgeLibraryReturnState
  role?: Role | null
  originLocationKey?: string
}) {
  const presentation = buildDocumentResultPresentation(doc)
  const sourceBacked = presentation.ownership.key === 'source-backed'
  const Icon = sourceBacked ? BookOpenCheck : FileText
  const warning =
    presentation.syncHealth.key === 'stale' || presentation.syncHealth.key === 'conflict'
  const focusedReturnState = {
    returnTo: { ...returnState.returnTo, focusId: doc.id },
  }
  const captureReturn = (event: MouseEvent<HTMLElement>) => {
    const safeKey = returnLocationKey(returnState.returnTo.pathname, returnState.returnTo.search)
    const scrollContainer = event.currentTarget.closest<HTMLElement>(
      '[data-return-scroll-container]',
    )
    captureDocumentReturn(safeKey, doc.id, scrollContainer)
    if (originLocationKey && originLocationKey !== safeKey) {
      captureDocumentReturn(originLocationKey, doc.id, scrollContainer)
    }
  }
  const authorityMetadata = getDocumentSourceAuthorityMetadata(doc)

  return (
    <article
      className={cn('aw-knowledge-row', warning && 'has-sync-warning')}
      data-return-focus-id={doc.id}
      tabIndex={-1}
    >
      <span className="aw-knowledge-row-icon" aria-hidden>
        <Icon className="h-4 w-4" />
      </span>
      <span className="aw-knowledge-row-main">
        <span className="aw-knowledge-row-title">
          <Link to={documentReadPath(doc)} state={focusedReturnState} onClick={captureReturn}>
            {presentation.title}
          </Link>
          <DocumentResultTerms presentation={presentation} />
        </span>
        <span className="aw-knowledge-row-excerpt">
          {presentation.excerpt || 'No description available.'}
        </span>
        <DocumentResultDetails presentation={presentation} updatedAt={doc.updatedAt} />
      </span>
      <div className="aw-knowledge-row-actions">
        {authorityMetadata && (role === 'admin' || role === 'editor') && (
          <SourceAuthorityReview
            role={role}
            documentId={doc.id}
            documentTitle={doc.title}
            sourceId={authorityMetadata.sourceId}
            initialMetadata={authorityMetadata}
            variant="library"
          />
        )}
        {canEdit && (
          <Link
            to={documentEditPath(doc)}
            state={focusedReturnState}
            onClick={captureReturn}
            className="aw-knowledge-row-action"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
            Edit
          </Link>
        )}
        <Link
          to={documentReadPath(doc)}
          state={focusedReturnState}
          onClick={captureReturn}
          className="aw-knowledge-row-action primary"
        >
          Open
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
      </div>
    </article>
  )
}
